/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Checkbox, Modal, Spin, Tag, Tooltip } from "antd";
import { useSnackbar } from "notistack";
import {
  Layers,
  FileText,
  History,
  Briefcase,
  ShieldCheck,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Info,
  ArrowRight,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import {
  getSubscriptionAddons,
  updateSubscriptionAddons,
} from "../../utils/organization";
import { getDefaultMethod, getPaymentMethods } from "../../utils/payment_methods";

const ManageAddonsModal = ({
  open,
  onClose,
  organization,
  onSuccess,
  onAddNewCard,
  paymentCards: propPaymentCards,
  defaultPaymentMethodId: propDefaultPaymentMethodId,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [initialPlan, setInitialPlan] = useState({
    accounts: 1,
    files: 0,
    history_and_notes: 0,
    payroll: 0,
  });

  const [selectedPlan, setSelectedPlan] = useState({
    accounts: 1,
    files: 0,
    history_and_notes: 0,
    payroll: 0,
  });

  const [isPayrollEligible, setIsPayrollEligible] = useState(false);
  const [paymentCards, setPaymentCards] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);

  const orgId = organization?.id;
  const orgName = organization?.account || organization?.name || "Organization";
  const provider = (organization?.provider || "xero").toLowerCase();
  const isQbo = provider === "qbo";
  const providerBrand = isQbo ? "QuickBooks Online" : "Xero";

  // Calculate pricing
  const baseAccountCost = 9.95;
  const filesCost = 4.0;
  const historyNotesCost = 4.0;
  const payrollCost = 0.0;

  const computeTotal = (plan) => {
    let sum = baseAccountCost;
    if (plan.files) sum += filesCost;
    if (plan.history_and_notes) sum += historyNotesCost;
    if (plan.payroll) sum += payrollCost;
    return sum;
  };

  const initialTotal = computeTotal(initialPlan);
  const newTotal = computeTotal(selectedPlan);
  const priceDiff = newTotal - initialTotal;

  const isUnchanged =
    Boolean(selectedPlan.files) === Boolean(initialPlan.files) &&
    Boolean(selectedPlan.history_and_notes) === Boolean(initialPlan.history_and_notes) &&
    Boolean(selectedPlan.payroll) === Boolean(initialPlan.payroll);

  const isUpgrade = Boolean(
    priceDiff > 0 ||
      (!isUnchanged &&
        ((!initialPlan.files && Boolean(selectedPlan.files)) ||
          (!initialPlan.history_and_notes && Boolean(selectedPlan.history_and_notes)) ||
          (!initialPlan.payroll && Boolean(selectedPlan.payroll))))
  );
  const isDowngrade = Boolean(priceDiff < 0);

  // Sync with prop payment cards if provided from parent
  useEffect(() => {
    if (propPaymentCards && propPaymentCards.length > 0) {
      setPaymentCards(propPaymentCards);
      const defId = propDefaultPaymentMethodId || null;
      if (defId) setDefaultPaymentMethodId(defId);
      if (defId && propPaymentCards.some((c) => c.id === defId)) {
        setSelectedPaymentMethodId(defId);
      } else if (!selectedPaymentMethodId && propPaymentCards.length > 0) {
        setSelectedPaymentMethodId(propPaymentCards[0].id);
      }
    }
  }, [propPaymentCards, propDefaultPaymentMethodId]);

  const loadAddonsAndCards = async () => {
    if (!orgId || !open) return;
    setLoading(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;

      // 1. Fetch current subscription add-ons
      const addonsRes = await getSubscriptionAddons(token, orgId, provider);
      if (addonsRes?.data?.success) {
        const cur = addonsRes.data.current_plan || {};
        const planObj = {
          accounts: 1,
          files: cur.files ? 1 : 0,
          history_and_notes: cur.history_and_notes ? 1 : 0,
          payroll: cur.payroll ? 1 : 0,
        };
        setInitialPlan(planObj);
        setSelectedPlan(planObj);
        setIsPayrollEligible(Boolean(addonsRes.data.is_payroll_eligible));

        if (addonsRes.data.default_payment_method) {
          setDefaultPaymentMethodId(addonsRes.data.default_payment_method);
          if (!selectedPaymentMethodId) {
            setSelectedPaymentMethodId(addonsRes.data.default_payment_method);
          }
        }
      }

      // 2. Fetch payment cards if not passed via props
      if (!propPaymentCards || propPaymentCards.length === 0) {
        try {
          const cardsRes = await getPaymentMethods(token);
          if (cardsRes?.data && Array.isArray(cardsRes.data)) {
            setPaymentCards(cardsRes.data);
            const defRes = await getDefaultMethod(token);
            const defId =
              defRes?.data?.data?.default_payment_method ||
              defRes?.data?.default_payment_method ||
              cardsRes.data[0]?.id;
            if (defId) {
              setDefaultPaymentMethodId(defId);
              setSelectedPaymentMethodId(defId);
            } else if (cardsRes.data.length > 0) {
              setSelectedPaymentMethodId(cardsRes.data[0].id);
            }
          }
        } catch (e) {
          console.error("ManageAddonsModal: error loading cards", e);
        }
      }
    } catch (err) {
      console.error("ManageAddonsModal: error loading details", err);
      enqueueSnackbar("Failed to load current subscription add-ons.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAddonsAndCards();
    }
  }, [open, orgId]);

  const handleToggleModule = (key, checked) => {
    setSelectedPlan((prev) => ({
      ...prev,
      [key]: checked ? 1 : 0,
    }));
  };

  const handleSave = async () => {
    if (isUnchanged) {
      onClose();
      return;
    }

    if (isUpgrade && paymentCards.length === 0 && !selectedPaymentMethodId) {
      enqueueSnackbar(
        "Please add or select a payment card to upgrade subscription add-ons.",
        { variant: "warning" }
      );
      if (onAddNewCard) {
        onAddNewCard();
      }
      return;
    }

    setSubmitting(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;

      const payload = {
        org_id: orgId,
        provider,
        files: Boolean(selectedPlan.files),
        history_and_notes: Boolean(selectedPlan.history_and_notes),
        payroll: Boolean(selectedPlan.payroll),
        payment_method_id: selectedPaymentMethodId || undefined,
      };

      const res = await updateSubscriptionAddons(token, payload);
      if (res?.data?.success) {
        enqueueSnackbar(
          res.data.message || "Subscription add-ons updated successfully!",
          { variant: "success" }
        );
        setInitialPlan(selectedPlan);
        if (onSuccess) {
          onSuccess(res.data.plan || selectedPlan);
        }
        onClose();
      } else {
        enqueueSnackbar(
          res?.data?.message || "Failed to update subscription add-ons.",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error("Error saving subscription add-ons:", err);
      enqueueSnackbar(
        err?.response?.data?.message ||
          "An error occurred while updating subscription add-ons.",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={640}
      className="manage-addons-modal"
      title={
        <div className="flex items-center gap-2.5 pb-2 border-b border-gray-100">
          <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0">
              Manage Subscription Add-ons
            </h3>
            <p className="text-xs text-gray-500 m-0 mt-0.5">
              Upgrade or downgrade optional backup modules for your organization
            </p>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="py-16 text-center">
          <Spin size="large" />
          <p className="text-sm text-gray-500 mt-4">
            Loading current subscription plan...
          </p>
        </div>
      ) : (
        <div className="py-4 space-y-5">
          {/* Organization Pill Banner */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-2.5">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  isQbo
                    ? "bg-green-100 text-green-800"
                    : "bg-sky-100 text-sky-800"
                }`}
              >
                {providerBrand}
              </span>
              <span className="font-semibold text-sm text-gray-900">
                {orgName}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <ShieldCheck className="w-4 h-4 text-green-600" />
              <span>Active Subscription</span>
            </div>
          </div>

          {isQbo ? (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold m-0">QuickBooks Online Plan</p>
                  <p className="text-xs text-amber-800 mt-1 mb-0">
                    QuickBooks Online organizations automatically include all supported data modules under the primary plan. Additional module add-ons are only applicable to Xero organizations.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Modules List */}
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-0.5">
                  Available Modules
                </div>

                {/* 1. Account (Core) - Always active */}
                <div className="p-3.5 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-700">
                      <ShieldCheck className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          Account Data & Transactions
                        </span>
                        <Tag color="blue" className="text-xs border-0 m-0">
                          Base Module
                        </Tag>
                      </div>
                      <p className="text-xs text-gray-500 m-0 mt-0.5">
                        Invoices, bills, contacts, chart of accounts, and manual journals
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      $9.95
                    </span>
                    <span className="text-xs text-gray-500">/mo</span>
                  </div>
                </div>

                {/* 2. Files Module */}
                <div
                  onClick={() => handleToggleModule("files", !selectedPlan.files)}
                  className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                    selectedPlan.files
                      ? "border-indigo-400 bg-indigo-50/40 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={Boolean(selectedPlan.files)}
                      onChange={(e) => handleToggleModule("files", e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          Files & Documents
                        </span>
                        {Boolean(initialPlan.files) && (
                          <Tag color="default" className="text-[10px] m-0">
                            Currently Active
                          </Tag>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 m-0 mt-0.5">
                        Backup all files, attached receipts, and document library from Xero
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      +$4.00
                    </span>
                    <span className="text-xs text-gray-500">/mo</span>
                  </div>
                </div>

                {/* 3. History and Notes Module */}
                <div
                  onClick={() =>
                    handleToggleModule(
                      "history_and_notes",
                      !selectedPlan.history_and_notes
                    )
                  }
                  className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                    selectedPlan.history_and_notes
                      ? "border-indigo-400 bg-indigo-50/40 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={Boolean(selectedPlan.history_and_notes)}
                      onChange={(e) =>
                        handleToggleModule("history_and_notes", e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                      <History className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          History & Notes
                        </span>
                        {Boolean(initialPlan.history_and_notes) && (
                          <Tag color="default" className="text-[10px] m-0">
                            Currently Active
                          </Tag>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 m-0 mt-0.5">
                        Audit trail, note entries, and change records across all transactions
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      +$4.00
                    </span>
                    <span className="text-xs text-gray-500">/mo</span>
                  </div>
                </div>

                {/* 4. Payroll Module (if eligible) */}
                <div
                  onClick={() => {
                    if (isPayrollEligible) {
                      handleToggleModule("payroll", !selectedPlan.payroll);
                    }
                  }}
                  className={`p-3.5 rounded-xl border transition-all ${
                    !isPayrollEligible
                      ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                      : selectedPlan.payroll
                      ? "border-indigo-400 bg-indigo-50/40 shadow-sm cursor-pointer"
                      : "border-gray-200 hover:border-gray-300 bg-white cursor-pointer"
                  } flex items-center justify-between`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={Boolean(selectedPlan.payroll)}
                      disabled={!isPayrollEligible}
                      onChange={(e) => handleToggleModule("payroll", e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          Payroll Records
                        </span>
                        {!isPayrollEligible ? (
                          <Tag color="default" className="text-[10px] m-0">
                            AU, GB & NZ only
                          </Tag>
                        ) : Boolean(initialPlan.payroll) ? (
                          <Tag color="default" className="text-[10px] m-0">
                            Currently Active
                          </Tag>
                        ) : null}
                      </div>
                      <p className="text-xs text-gray-500 m-0 mt-0.5">
                        Pay runs, employee records, timesheets, and payslips
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      +$0.00
                    </span>
                    <span className="text-xs text-gray-500">/mo</span>
                  </div>
                </div>
              </div>

              {/* Cost Summary Box */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-indigo-50/30 border border-gray-200/80">
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200/60">
                  <span className="text-xs font-medium text-gray-500">
                    Current Monthly Subscription
                  </span>
                  <span className="text-sm font-semibold text-gray-700">
                    ${initialTotal.toFixed(2)}/month
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider block">
                      New Monthly Total
                    </span>
                    <span className="text-xs text-gray-500 mt-0.5 block">
                      {isUnchanged
                        ? "No change to current billing"
                        : isUpgrade
                        ? "Prorated charge applies immediately"
                        : "Credit applies to next billing invoice"}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      {priceDiff !== 0 && (
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            priceDiff > 0
                              ? "bg-amber-100 text-amber-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {priceDiff > 0 ? (
                            <>
                              <TrendingUp className="w-3 h-3" />
                              +${priceDiff.toFixed(2)}
                            </>
                          ) : (
                            <>
                              <TrendingDown className="w-3 h-3" />
                              -${Math.abs(priceDiff).toFixed(2)}
                            </>
                          )}
                        </span>
                      )}
                      <span className="text-xl font-bold text-gray-900">
                        ${newTotal.toFixed(2)}
                      </span>
                      <span className="text-xs text-gray-500">/mo</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method Selector (if Upgrade and cards available) */}
              {Boolean(isUpgrade) && (
                <div className="p-3.5 rounded-xl border border-blue-200 bg-blue-50/40">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                      Payment Method for Proration
                    </span>
                    {onAddNewCard && (
                      <button
                        type="button"
                        onClick={onAddNewCard}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 underline bg-transparent border-0 cursor-pointer p-0"
                      >
                        + Add new card
                      </button>
                    )}
                  </div>

                  {paymentCards.length > 0 ? (
                    <div className="space-y-1.5">
                      {paymentCards.map((card) => {
                        const isSelected = selectedPaymentMethodId === card.id;
                        const isDefault = defaultPaymentMethodId === card.id;
                        return (
                          <div
                            key={card.id}
                            onClick={() => setSelectedPaymentMethodId(card.id)}
                            className={`p-2.5 rounded-lg border text-xs cursor-pointer flex items-center justify-between transition-all ${
                              isSelected
                                ? "border-blue-500 bg-white shadow-xs font-medium text-blue-900"
                                : "border-gray-200 bg-white/70 hover:bg-white text-gray-700"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="capitalize font-semibold">
                                {card.card?.brand || "Card"}
                              </span>
                              <span>•••• {card.card?.last4}</span>
                              <span className="text-gray-400">
                                Exp {card.card?.exp_month}/{card.card?.exp_year}
                              </span>
                              {isDefault && (
                                <Tag color="blue" className="text-[10px] m-0">
                                  Default
                                </Tag>
                              )}
                            </div>
                            {isSelected && (
                              <CheckCircle2 className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 m-0">
                      Using customer default payment method on file.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={submitting}
              disabled={isQbo || isUnchanged}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isUnchanged
                ? "No Changes"
                : isUpgrade
                ? "Confirm & Upgrade Plan"
                : "Confirm & Downgrade Plan"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ManageAddonsModal;
